"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { toast } from "sonner"
import { submitApplication } from "@/app/actions"
import { useEffect } from "react"
import { trackEvent } from "@/lib/tracking"

import ReactMarkdown from "react-markdown"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"

interface ApplicationFormProps {
    serviceType?: string
    kvkkContent?: string
}

export function ApplicationForm({ serviceType = 'visa', kvkkContent }: ApplicationFormProps) {
    const [loading, setLoading] = useState(false)
    const [submitted, setSubmitted] = useState(false)

    // ... (rest of the component logic)

    useEffect(() => {
        // Track funnel step: Form Opened / Started
        trackEvent("view_item", {
            item_id: "application_form",
            item_name: "Application Form",
            item_category: serviceType,
        });

        // Meta specific contact/initiate event
        trackEvent("Contact", {
            content_category: "form",
            content_name: serviceType
        });
    }, [serviceType]);


    async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault()
        setLoading(true)

        const form = event.currentTarget
        const formData = new FormData(form)
        // Ensure serviceType is included if missing
        if (!formData.get("serviceType")) {
            formData.append("serviceType", serviceType)
        }

        try {
            const result = await submitApplication(formData)

            if (!result.success) {
                console.error("Submission Error:", result.error)
                toast.error("Başvuru gönderilemedi", {
                    description: "Lütfen daha sonra tekrar deneyin veya bizi arayın."
                })
            } else {
                setSubmitted(true)

                // Track funnel step: Lead (Success)
                trackEvent("Lead", {
                    content_name: serviceType,
                    content_category: "application",
                    value: 0, // No direct value yet
                    currency: "TRY"
                });

                // GA4 specific lead event
                trackEvent("generate_lead", {
                    service_type: serviceType,
                });

                toast.success("Başvurunuz Başarıyla Alındı!", {
                    description: "Uzman danışmanlarımız başvurunuzu inceleyerek 1 iş günü içerisinde size dönüş yapacaktır. Lütfen telefonunuzu açık tutunuz.",
                    duration: 6000
                })
                form.reset()
            }
        } catch (err) {
            console.error("Form submit error:", err)
            toast.error("Bir hata oluştu", {
                description: "Lütfen tekrar deneyin."
            })
        } finally {
            setLoading(false)
        }
    }

    // Show success message if submitted
    if (submitted) {
        return (
            <Card className="w-full shadow-lg border-t-4 border-t-primary">
                <CardContent className="pt-8 pb-8">
                    <div className="text-center space-y-6">
                        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-2xl font-bold text-primary mb-2">
                                Başvurunuz Alındı!
                            </h3>
                            <p className="text-primary/80 text-lg mb-4">
                                Teşekkür ederiz
                            </p>
                        </div>
                        <div className="bg-background rounded-lg p-6 border border-primary/20">
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                Uzman danışmanlarımız <strong>başvurunuzu inceleyerek</strong> en geç <strong className="text-primary">1 iş günü içerisinde</strong> sizinle iletişime geçecektir.
                            </p>
                            <p className="text-muted-foreground text-sm">
                                📞 Lütfen telefonunuzu açık tutunuz
                            </p>
                        </div>
                        <Button
                            onClick={() => setSubmitted(false)}
                            variant="outline"
                            className="mt-4"
                        >
                            Yeni Başvuru Yap
                        </Button>
                    </div>
                </CardContent>
            </Card>
        )
    }

    return (
        <Card className="w-full shadow-lg border-t-4 border-t-primary">
            <CardHeader>
                <CardTitle className="text-2xl font-bold text-primary text-center">
                    {(serviceType?.toLowerCase() === 'denklik' || serviceType === 'Denklik Danışmanlığı') ? 'Denklik Ön Değerlendirme Formu' : 'Vize Başvuru Formu'}
                </CardTitle>
                <CardDescription>
                    Aşağıdaki formu eksiksiz doldurarak süreci başlatabilirsiniz.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6" data-form-name="application_form">
                    <input type="hidden" name="serviceType" value={serviceType} />
                    {/* 1. Satır: Ad Soyad - Telefon */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="fullName">Ad Soyad <span className="text-destructive">*</span></Label>
                            <Input id="fullName" name="fullName" required placeholder="Pasaporttaki tam isminiz" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="phone">Telefon Numarası <span className="text-destructive">*</span></Label>
                            <Input id="phone" name="phone" type="tel" required placeholder="0533 139 14 44" />
                        </div>
                    </div>

                    {/* 2. Satır: E-posta - Yaş - Şehir */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">E-posta Adresi</Label>
                            <Input id="email" name="email" type="email" placeholder="iletisim@ornek.com" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="age">Yaşınız <span className="text-destructive">*</span></Label>
                            <Input id="age" name="age" type="number" required placeholder="Örn: 30" min="1" max="100" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="city">Yaşadığınız Şehir <span className="text-destructive">*</span></Label>
                            <Input id="city" name="city" required placeholder="Örn: Ankara" />
                        </div>
                    </div>

                    {/* 3. Satır: Ülke - Vize Türü */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="targetCountry">Hedef Ülke <span className="text-destructive">*</span></Label>
                            <select
                                id="targetCountry"
                                name="targetCountry"
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                required
                            >
                                <option value="">Seçiniz</option>
                                <option value="almanya">Almanya</option>
                                <option value="fransa">Fransa</option>
                                <option value="italya">İtalya</option>
                                <option value="hollanda">Hollanda</option>
                                <option value="ispanya">İspanya</option>
                                <option value="amerika">Amerika (ABD)</option>
                                <option value="ingiltere">İngiltere (UK)</option>
                                <option value="kanada">Kanada</option>
                                <option value="diger">Diğer</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="visaType">{(serviceType?.toLowerCase() === 'denklik' || serviceType === 'Denklik Danışmanlığı') ? 'Hizmet Türü' : 'Vize Türü'} <span className="text-destructive">*</span></Label>
                            {(serviceType?.toLowerCase() === 'denklik' || serviceType === 'Denklik Danışmanlığı') ? (
                                <>
                                    <div className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm text-slate-500 items-center">
                                        Denklik Danışmanlığı
                                    </div>
                                    <input type="hidden" name="visaType" value="denklik" />
                                </>
                            ) : (
                                <select
                                    id="visaType"
                                    name="visaType"
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                    required
                                >
                                    <option value="">Seçiniz</option>
                                    <option value="turistik">Turistik Vize</option>
                                    <option value="ticari">Ticari Vize</option>
                                    <option value="aile">Aile Ziyareti</option>
                                    <option value="ogrenci">Öğrenci / Eğitim</option>
                                    <option value="calisma">Göç Danışmanlığı</option>
                                    <option value="denklik">Denklik Danışmanlığı</option>
                                </select>
                            )}
                        </div>
                    </div>

                    {/* 4. Satır: Meslek */}
                    <div className="space-y-2">
                        <Label htmlFor="profession">Meslek / Çalışma Durumu <span className="text-destructive">*</span></Label>
                        <Input
                            id="profession"
                            name="profession"
                            required
                            placeholder="Örn. Yazılım Mühendisi, Öğrenci, Emekli..."
                        />
                    </div>

                    {/* 5. Satır: Notlar */}
                    <div className="space-y-2">
                        <Label htmlFor="notes">Ek Açıklamalar</Label>
                        <Textarea
                            id="notes"
                            name="notes"
                            placeholder="Varsa özel durumunuzu veya sorularınızı kısaca belirtiniz..."
                            className="min-h-[80px]"
                        />
                    </div>

                    {/* 6. Satır: KVKK */}
                    <div className="flex items-start space-x-3 pt-4 border-t">
                        <input
                            type="checkbox"
                            id="kvkk"
                            name="kvkk"
                            required
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        />
                        <div className="grid gap-1.5 leading-none">
                            <Label
                                htmlFor="kvkk"
                                className="text-sm font-medium leading-normal cursor-pointer text-muted-foreground"
                            >
                                Kişisel verilerimin 6698 sayılı KVKK kapsamında işlenmesine ilişkin aydınlatma metnini okudum, kabul ediyorum.
                            </Label>

                            {kvkkContent ? (
                                <Dialog>
                                    <DialogTrigger asChild>
                                        <p className="text-xs text-primary underline cursor-pointer hover:text-primary/80 w-fit">
                                            Aydınlatma Metnini Gör
                                        </p>
                                    </DialogTrigger>
                                    <DialogContent className="max-w-xl max-h-[85vh]">
                                        <DialogHeader>
                                            <DialogTitle>KVKK Aydınlatma Metni</DialogTitle>
                                            <DialogDescription>
                                                Kişisel verilerinizin işlenmesine ilişkin aydınlatma metni.
                                            </DialogDescription>
                                        </DialogHeader>
                                        <ScrollArea className="h-[60vh] mt-4 pr-4 border rounded-md p-4 bg-muted/10">
                                            <div className="prose prose-sm prose-slate dark:prose-invert max-w-none">
                                                <ReactMarkdown>{kvkkContent}</ReactMarkdown>
                                            </div>
                                        </ScrollArea>
                                    </DialogContent>
                                </Dialog>
                            ) : (
                                <p className="text-xs text-primary underline cursor-pointer hover:text-primary/80">
                                    Aydınlatma Metnini Gör
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="pt-2">
                        <Button type="submit" className="w-full text-lg h-12 font-bold shadow-md hover:shadow-lg transition-all" disabled={loading}>
                            {loading ? "Gönderiliyor..." : "Başvuruyu Gönder"}
                        </Button>
                    </div>
                </form>
            </CardContent>
        </Card>
    )
}
